import { RestApiResultVoucherWithStats, VoucherWithStats } from '@flipdish/api-client-typescript';
import axios from 'axios';

import { getApiEndPoint } from '../../helpers/apibase';
import { store } from '../../helpers/store';
import { flagService } from '../../services/flagService';
import { mapServerError } from '../../services/utils/serverErrorMapper';

const baseUrl = getApiEndPoint();
const portalApi = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
});

// #region getVoucherById
export type GetByIdProps = {
  voucherId: number;
};
export async function getById({ voucherId }: GetByIdProps): Promise<VoucherWithStats> {
  try {
    const msg = await portalApi.get<RestApiResultVoucherWithStats>(
      `/api/v1.0/vouchers/${encodeURIComponent(voucherId.toString())}`
    );

    // Server dates are UTC but arrive with no offset, which parses as browser-local; append 'Z' to
    // force UTC. Skipped when a marker is already present so a future BE change can't produce "...ZZ".
    // Typed Date to match the generated client, though the payload is really a string.
    const hasTimezoneMarker = (raw: string) => /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
    const parseAsUtc = (raw: Date | undefined) => {
      const rawString = raw as unknown as string;
      // Absent stays absent: "undefinedZ" would parse to an Invalid Date, which isn't undefined and so
      // would flow on into the next update payload and render as "Invalid date".
      if (!rawString) {
        return undefined;
      }
      return new Date(hasTimezoneMarker(rawString) ? rawString : `${rawString}Z`);
    };

    return {
      ...msg.data.Data,
      // Flag off: flatten to UTC noon so the intended day survives any viewer's timezone. Wrong once
      // the flag is on, where the stored instant is store-local end-of-day and so can already fall on
      // the next UTC day — flattening would display, then persist, that wrong day.
      ExpiryDate: flagService.isFlagOn(store.getState(), 'expiryUsesStoreTimezone')
        ? parseAsUtc(msg.data.Data.ExpiryDate)
        : (() => {
            const d = parseAsUtc(msg.data.Data.ExpiryDate);
            if (!d) {
              return undefined;
            }
            return new Date(
              Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0, 0)
            );
          })(),
      // We need to parse the start date as a UTC date
      StartDate: parseAsUtc(msg.data.Data.StartDate),
    };
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}
// #endregion
